/** Minouko controls. Native fields remain the submitted source of truth. */
(() => {
  'use strict';
  const desktop = window.matchMedia('(min-width: 900px) and (hover: hover)');
  const selects = new Set();
  let activeSelect = null;
  let uid = 0;

  function enhanceSelect(root) {
    if (root.dataset.uiReady) return;
    const select = root.querySelector('select');
    if (!select || select.multiple || select.size > 1) return;
    root.dataset.uiReady = 'true';
    const prefix = `ui-select-${++uid}`;
    const trigger = document.createElement('button');
    trigger.type = 'button';
    trigger.className = 'ui-select__trigger';
    trigger.id = `${prefix}-trigger`;
    trigger.setAttribute('role', 'combobox');
    trigger.setAttribute('aria-haspopup', 'listbox');
    trigger.setAttribute('aria-controls', `${prefix}-list`);
    trigger.setAttribute('aria-expanded', 'false');
    const label = select.labels && select.labels[0];
    if (label) {
      if (!label.id) label.id = `${prefix}-label`;
      trigger.setAttribute('aria-labelledby', label.id);
      label.addEventListener('click', e => {
        if (!desktop.matches) return;
        e.preventDefault(); trigger.focus();
      });
    } else trigger.setAttribute('aria-label', select.getAttribute('aria-label') || 'Choisir une option');
    const value = document.createElement('span');
    const chevron = document.createElement('span');
    chevron.className = 'ui-chevron';
    chevron.setAttribute('aria-hidden', 'true');
    trigger.append(value, chevron);
    const list = document.createElement('ul');
    list.id = `${prefix}-list`;
    list.className = 'ui-select__list';
    list.setAttribute('role', 'listbox');
    list.setAttribute('aria-label', label ? label.textContent.trim() : 'Options');
    list.hidden = true;
    const options = [...select.options];
    const items = options.map((option, index) => {
      const item = document.createElement('li');
      item.id = `${prefix}-${index}`;
      item.className = 'ui-select__option';
      item.setAttribute('role', 'option');
      item.setAttribute('aria-selected', 'false');
      item.textContent = option.text;
      item.addEventListener('pointerdown', e => e.preventDefault());
      item.addEventListener('pointermove', () => { if (!option.disabled) highlight(index); });
      item.addEventListener('click', () => { if (!option.disabled) { commit(index); close(); trigger.focus(); } });
      list.append(item); return item;
    });
    root.append(trigger, list);
    let index = Math.max(0, select.selectedIndex);
    let buffer = '';
    let lastType = 0;
    const originalTabindex = select.getAttribute('tabindex');
    const originalAriaHidden = select.getAttribute('aria-hidden');
    const controller = {root, trigger, close, mode};
    selects.add(controller);

    function sync() {
      value.textContent = options[select.selectedIndex]?.text || '';
      trigger.disabled = select.disabled;
      items.forEach((item, i) => {
        item.setAttribute('aria-selected', String(i === select.selectedIndex));
        item.setAttribute('aria-disabled', String(options[i].disabled));
      });
    }
    function highlight(next) {
      if (next < 0 || next >= items.length || options[next].disabled) return;
      index = next;
      items.forEach((item, i) => item.toggleAttribute('data-active', i === index));
      trigger.setAttribute('aria-activedescendant', items[index].id);
      const top = items[index].offsetTop;
      const bottom = top + items[index].offsetHeight;
      if (top < list.scrollTop) list.scrollTop = top;
      else if (bottom > list.scrollTop + list.clientHeight) list.scrollTop = bottom - list.clientHeight;
    }
    function open() {
      if (trigger.disabled || !desktop.matches) return;
      if (activeSelect && activeSelect !== controller) activeSelect.close();
      activeSelect = controller;
      list.hidden = false;
      trigger.setAttribute('aria-expanded', 'true');
      root.toggleAttribute('data-above', innerHeight - trigger.getBoundingClientRect().bottom < Math.min(list.scrollHeight, 320) + 12 && trigger.getBoundingClientRect().top > 330);
      highlight(select.selectedIndex >= 0 ? select.selectedIndex : options.findIndex(o => !o.disabled));
    }
    function close() {
      list.hidden = true;
      trigger.setAttribute('aria-expanded', 'false');
      trigger.removeAttribute('aria-activedescendant');
      buffer = '';
      if (activeSelect === controller) activeSelect = null;
    }
    function commit(next) {
      if (options[next]?.disabled || !options[next]) return;
      if (select.selectedIndex !== next) {
        select.selectedIndex = next;
        select.dispatchEvent(new Event('input', {bubbles: true}));
        select.dispatchEvent(new Event('change', {bubbles: true}));
      }
      sync();
    }
    function mode() {
      if (!root.isConnected) { close(); selects.delete(controller); return; }
      close();
      const custom = desktop.matches;
      root.toggleAttribute('data-enhanced', custom);
      trigger.hidden = !custom;
      if (custom) { select.tabIndex = -1; select.setAttribute('aria-hidden', 'true'); }
      else {
        if (originalTabindex === null) select.removeAttribute('tabindex'); else select.setAttribute('tabindex', originalTabindex);
        if (originalAriaHidden === null) select.removeAttribute('aria-hidden'); else select.setAttribute('aria-hidden', originalAriaHidden);
      }
      sync();
    }
    trigger.addEventListener('click', () => list.hidden ? open() : close());
    trigger.addEventListener('keydown', e => {
      const enabled = options.map((o, i) => o.disabled ? -1 : i).filter(i => i !== -1);
      if (!enabled.length) return;
      if (e.key === 'Escape' && !list.hidden) { e.preventDefault(); e.stopPropagation(); close(); return; }
      if (e.key === 'Tab') { if (!list.hidden) { commit(index); close(); } return; }
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        if (list.hidden) open(); else { commit(index); close(); }
        return;
      }
      if (['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(e.key)) {
        e.preventDefault();
        const wasClosed = list.hidden;
        if (wasClosed) open();
        if (e.key === 'Home') highlight(enabled[0]);
        else if (e.key === 'End') highlight(enabled.at(-1));
        else if (!wasClosed) highlight(enabled[Math.max(0, Math.min(enabled.length - 1, enabled.indexOf(index) + (e.key === 'ArrowDown' ? 1 : -1)))]);
        return;
      }
      if (e.key.length === 1 && !e.ctrlKey && !e.altKey && !e.metaKey) {
        e.preventDefault();
        if (list.hidden) open();
        const now = Date.now();
        buffer = now - lastType > 900 ? e.key : buffer + e.key;
        lastType = now;
        const normalize = t => t.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
        const query = [...buffer].every(c => c === buffer[0]) ? buffer[0] : buffer;
        const order = query.length > 1 ? [index, ...enabled.filter(i => i !== index)] : [...enabled.filter(i => i > index), ...enabled.filter(i => i <= index)];
        const next = order.find(i => normalize(options[i].text).startsWith(normalize(query)));
        if (next !== undefined) highlight(next);
      }
    });
    select.addEventListener('change', sync);
    select.form?.addEventListener('reset', () => setTimeout(sync, 0));
    mode();
  }

  function init(scope = document) { scope.querySelectorAll('[data-ui-select]').forEach(enhanceSelect); }
  document.addEventListener('pointerdown', e => { if (activeSelect && !activeSelect.root.contains(e.target)) activeSelect.close(); });
  document.addEventListener('focusin', e => { if (activeSelect && !activeSelect.root.contains(e.target)) activeSelect.close(); });
  desktop.addEventListener('change', () => selects.forEach(s => s.mode()));
  document.addEventListener('shopify:section:load', e => init(e.target));
  document.addEventListener('shopify:section:unload', e => selects.forEach(s => { if (e.target.contains(s.root)) { s.close(); selects.delete(s); } }));

  // Inline field feedback supplements the browser's validation, without replacing it.
  function isField(el) {
    return el.matches?.('input:not([type="hidden"], [type="checkbox"], [type="radio"]), textarea') && el.closest('main, .ft, .drawer, .ovl');
  }
  function clearError(field) {
    const errorId = field.dataset.uiError;
    if (!errorId) return;
    document.getElementById(errorId)?.remove();
    const described = (field.getAttribute('aria-describedby') || '').split(' ').filter(id => id && id !== errorId);
    if (described.length) field.setAttribute('aria-describedby', described.join(' ')); else field.removeAttribute('aria-describedby');
    field.removeAttribute('aria-invalid');
    delete field.dataset.uiError;
  }
  function showError(field) {
    if (field.validity.valid) { clearError(field); return; }
    let error = document.getElementById(field.dataset.uiError);
    if (!error) {
      error = document.createElement('p');
      error.id = `ui-field-error-${++uid}`;
      error.className = 'ui-field-error';
      error.setAttribute('aria-live', 'polite');
      const group = field.closest('.stepper-field, .qty-step, .ft__row');
      (group || field).after(error);
      field.dataset.uiError = error.id;
      field.setAttribute('aria-describedby', [field.getAttribute('aria-describedby'), error.id].filter(Boolean).join(' '));
    }
    field.setAttribute('aria-invalid', 'true');
    const validity = field.validity;
    error.textContent = validity.valueMissing ? 'Renseignez ce champ pour continuer.'
      : validity.typeMismatch && field.type === 'email' ? 'Saisissez une adresse e-mail valide.'
      : validity.rangeUnderflow ? `Choisissez une valeur supérieure ou égale à ${field.min}.`
      : validity.rangeOverflow ? `Choisissez une valeur inférieure ou égale à ${field.max}.`
      : validity.badInput || validity.stepMismatch ? 'Saisissez une valeur valide.'
      : 'Vérifiez ce champ avant de continuer.';
  }
  document.addEventListener('invalid', e => { if (isField(e.target)) showError(e.target); }, true);
  document.addEventListener('focusout', e => { if (isField(e.target) && e.target.value) showError(e.target); });
  document.addEventListener('input', e => { if (isField(e.target) && e.target.dataset.uiError) { if (e.target.validity.valid) clearError(e.target); } });
  document.addEventListener('reset', e => { setTimeout(() => e.target.querySelectorAll('[data-ui-error]').forEach(clearError), 0); });
  init();
})();
